import { useEffect, useRef } from 'react';

const MatrixRain = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Canvas setup - ensure it covers the entire viewport
    const resizeCanvas = () => {
      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * devicePixelRatio;
      canvas.height = window.innerHeight * devicePixelRatio;

      // Scale back down using CSS
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';

      // Scale the drawing context so everything draws at the higher resolution
      ctx.scale(devicePixelRatio, devicePixelRatio);
    };

    resizeCanvas();

    // Samurai-inspired characters - Kanji for nobility, warrior, honor, blade, shadow
    const kanjiChars = '武剣道影侍魂刃桜月夜風火水木金土天地人心忍者的意志力光闇黒白赤';
    const chars = kanjiChars.split('');

    // Secondary characters - smaller symbols for depth
    const smallChars = '・。○●□■△▽◆◇★☆卍';

    // Drop settings
    const fontSize = 16;
    const columns = Math.floor(window.innerWidth / fontSize);
    const drops = [];
    const smallDrops = [];

    // Initialize drops
    for (let x = 0; x < columns; x++) {
      drops[x] = Math.random() * -100; // Start above screen
      smallDrops[x] = Math.random() * -200;
    }

    // Cherry blossom petals
    const petals = [];
    const maxPetals = 30;

    const createPetal = () => {
      return {
        x: Math.random() * window.innerWidth,
        y: -20,
        size: Math.random() * 4 + 2,
        speed: Math.random() * 0.5 + 0.3,
        rotation: Math.random() * 360,
        rotationSpeed: Math.random() * 2 - 1,
        opacity: Math.random() * 0.6 + 0.2,
        sway: Math.random() * 2 - 1
      };
    };

    // Initialize petals
    for (let i = 0; i < maxPetals; i++) {
      const petal = createPetal();
      petal.y = Math.random() * window.innerHeight;
      petals.push(petal);
    }

    // Fog layers
    let fogOpacity = 0;
    let fogDirection = 0.002;

    // Animation function
    const draw = () => {
      // Deep black background with slight transparency for trails
      ctx.fillStyle = 'rgba(5, 5, 8, 0.08)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // Draw subtle fog/mist layer
      fogOpacity += fogDirection;
      if (fogOpacity > 0.15 || fogOpacity < 0.02) {
        fogDirection *= -1;
      }

      // Create fog gradient
      const fogGradient = ctx.createRadialGradient(
        window.innerWidth / 2, window.innerHeight / 2, 0,
        window.innerWidth / 2, window.innerHeight / 2, window.innerWidth
      );
      fogGradient.addColorStop(0, `rgba(20, 20, 25, ${fogOpacity})`);
      fogGradient.addColorStop(0.5, `rgba(10, 10, 15, ${fogOpacity * 0.7})`);
      fogGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = fogGradient;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // Draw primary kanji characters (main matrix effect)
      ctx.font = fontSize + 'px "Noto Sans JP", "MS Gothic", monospace';

      for (let i = 0; i < drops.length; i++) {
        const y = drops[i] * fontSize;

        // Vary the character density for a more organic feel
        if (Math.random() > 0.3) {
          const text = chars[Math.floor(Math.random() * chars.length)];
          const x = i * fontSize;

          // Create gradient for each character - dark red to deep gray
          const gradient = ctx.createLinearGradient(x, y - fontSize, x, y);
          gradient.addColorStop(0, 'rgba(180, 30, 30, 0.9)'); // Deep red at top
          gradient.addColorStop(0.5, 'rgba(120, 20, 20, 0.7)');
          gradient.addColorStop(1, 'rgba(60, 60, 70, 0.5)'); // Gray at bottom

          ctx.fillStyle = gradient;
          ctx.fillText(text, x, y);

          // Add subtle glow effect to some characters
          if (Math.random() > 0.92) {
            ctx.shadowColor = 'rgba(180, 30, 30, 0.4)';
            ctx.shadowBlur = 8;
            ctx.fillText(text, x, y);
            ctx.shadowBlur = 0;
          }
        }

        // Move drop
        if (y > window.innerHeight && Math.random() > 0.975) {
          drops[i] = 0;
        } else {
          drops[i]++;
        }
      }

      // Draw smaller secondary characters for depth
      ctx.font = '10px "Noto Sans JP", "MS Gothic", monospace';
      for (let i = 0; i < smallDrops.length; i++) {
        const y = smallDrops[i] * 8;

        if (Math.random() > 0.5) {
          const text = smallChars[Math.floor(Math.random() * smallChars.length)];
          const x = i * fontSize * 2 + 20;

          ctx.fillStyle = 'rgba(80, 80, 90, 0.3)';
          ctx.fillText(text, x, y);
        }

        if (y > window.innerHeight && Math.random() > 0.985) {
          smallDrops[i] = 0;
        } else {
          smallDrops[i]++;
        }
      }

      // Draw cherry blossom petals
      petals.forEach((petal, index) => {
        ctx.save();
        ctx.translate(petal.x, petal.y);
        ctx.rotate((petal.rotation * Math.PI) / 180);
        ctx.globalAlpha = petal.opacity;

        // Draw petal shape (elongated oval)
        ctx.beginPath();
        ctx.ellipse(0, 0, petal.size, petal.size * 0.6, 0, 0, Math.PI * 2);

        // Gradient from dark red to pink
        const petalGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, petal.size);
        petalGradient.addColorStop(0, 'rgba(180, 40, 60, 0.9)');
        petalGradient.addColorStop(0.6, 'rgba(200, 80, 100, 0.7)');
        petalGradient.addColorStop(1, 'rgba(220, 120, 140, 0.4)');
        ctx.fillStyle = petalGradient;
        ctx.fill();

        // Subtle outline
        ctx.strokeStyle = 'rgba(150, 30, 50, 0.3)';
        ctx.lineWidth = 0.5;
        ctx.stroke();

        ctx.restore();

        // Update petal position
        petal.y += petal.speed;
        petal.rotation += petal.rotationSpeed;
        petal.x += Math.sin(petal.y * 0.02) * petal.sway;

        // Reset petal when it goes off screen
        if (petal.y > window.innerHeight + 20) {
          petals[index] = createPetal();
        }
      });

      // Add subtle scanline effect for cinematic feel
      ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
      for (let y = 0; y < window.innerHeight; y += 3) {
        ctx.fillRect(0, y, window.innerWidth, 1);
      }

      // Add vignette effect for dramatic lighting
      const vignetteGradient = ctx.createRadialGradient(
        window.innerWidth / 2, window.innerHeight / 2, window.innerHeight * 0.3,
        window.innerWidth / 2, window.innerHeight / 2, window.innerWidth * 0.8
      );
      vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      vignetteGradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.3)');
      vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 0.7)');
      ctx.fillStyle = vignetteGradient;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
    };

    // Start animation
    const interval = setInterval(draw, 35); // ~28fps for smoother animation

    // Handle resize
    const handleResize = () => {
      resizeCanvas();
      // Recalculate columns and reinitialize drops
      const newColumns = Math.floor(window.innerWidth / fontSize);
      drops.length = newColumns;
      smallDrops.length = newColumns;
      for (let x = 0; x < newColumns; x++) {
        if (drops[x] === undefined) {
          drops[x] = Math.random() * -100;
          smallDrops[x] = Math.random() * -200;
        }
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        zIndex: '-999',
        pointerEvents: 'none',
        background: '#050508',
        display: 'block'
      }}
    />
  );
};

export default MatrixRain;
